import dayjs from 'dayjs';
import {ClassificationObject} from '../classificationObject';
import {ClassificationXMLObject} from '../classificationXMLObject';
import {classificationOptionsOrder, disseminationUnclassifiedOptions, disseminationConfidentialOptions,
    disseminationSecretOptions, disseminationTopSecretOptions, disseminationOptionsOrder, relToOptionsOrder, sciConfidentialOptions,
    sciSecretOptions, sciTopSecretOptions, sciOptionsOrder, fgiOptionsOrder, acquisitionLacOptions, disseminableLacOptions,
    lacOptionsOrder, sigintOptionsOrder, topicOptionsOrder, regionOptionsOrder} from '../constants/definedTypes';

export class Utilities {
    static getSafe(fn, defaultVal) {
        try {
            return fn();
        } catch (e) {
            return defaultVal;
        }
    }

    static unescapeHTMLEntities(xmlString: string): string {
        if (xmlString) {
            xmlString = xmlString.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
        }
        return xmlString;
    }

    static escapeHTMLEntities(xmlString: string): string {
        if (xmlString) {
            xmlString = xmlString.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        }
        return xmlString;
    }

    static removeLineBreaks(xmlString: string): string {
        if (xmlString) {
            xmlString = xmlString.replace(/(\r\n|\n|\r|\t)/gm, '');
        }
        return xmlString;
    }

    static xmlToJson(xmlString: string): string {
        const convert = require('xml-js');
        const options = {compact: true, ignoreComment: true, spaces: 4};
        xmlString = Utilities.unescapeHTMLEntities(xmlString);
        return convert.xml2json(xmlString, options);
    }

    static jsonToXML(jsonString: string): string {
        const convert = require('xml-js');
        const options = {compact: true, ignoreComment: true, spaces: 0, fullTagEmptyElement: true};
        return convert.json2xml(jsonString, options);
    }

    static convertToLicense(string: string): string {
        return Utilities.escapeHTMLEntities(string).replace(/\s+/g, ' ');
    }

    static isDate(dateString: any): boolean {
        const date = dayjs(dateString, ['YYYYMMDD', 'MM-DD-YYYY', 'MM/DD/YYYY']);
        return date.isValid();
    }

    static classificationToXMLObject(classificationObject: ClassificationObject): ClassificationXMLObject {
        return ClassificationXMLObject.parse(classificationObject.toClassificationXMLJSONObject());
    }

    static xmlObjectToClassification(xmlObject: ClassificationXMLObject): ClassificationObject {
        return ClassificationObject.parse(xmlObject.toClassificationJSONObject());
    }

    // Takes an an array and an array with a defined sort order, returns sorted array
    // Optional parameter removeUnknown filters out elements that are not found in definedOrder array
    // If item is not found in definedOrder, item remains in same place.
    // arrayToSort: ['a', 'b', 'a', 'c'], definedOrder: ['b', 'c', 'a']
    // return ['b', 'c', 'a', 'a']
    static sortByDefinedOrder(arrayToSort: any, definedOrder: any, removeUnknown?: boolean): any[] {
        // Create an Object Array with Index for Sort Compare method
        // ['b', 'c', 'a'] = {'b':0, 'c':1, 'a':2}
        const sortByObject = definedOrder.reduce((objectArray, item, index) => {
            objectArray[item] = index;
            return objectArray;
        }, {});
        // Compares Two Objects based on above array index.
        // < 0 : Item goes before object  b:0 - c:1 = -1; b goes before c
        // 0 : Items are equally sorted   b:0 - b:0 = 0; items are equal no sort needed
        // > 0 : Item goes after object   a:2 - b:0 = 2; a goes after b
        let tempSortedArray = arrayToSort.sort((objectA, objectB) =>
            sortByObject[objectA] - sortByObject[objectB],
        );

        // Remove Unknown elements
        if (removeUnknown === true) {
            tempSortedArray = tempSortedArray.filter( (element) => definedOrder.includes(element));
        }

        return tempSortedArray;
    }

    // Takes a Classification Banner String and Returns a JSON Object
    static bannerStringToJSON(banner) {
        // Split Banner String on '//' and ' ; '
        // This is accomplished by first splitting on '//', joining those elements back together with ' ; '
        // and then re-splitting by ' ; ' again, to ensure we don't have to loop multiple times
        // .filter(Boolean) removes all falsy elements ("", 0, NaN, null, undefined, false)
        const bannerString = Utilities.getSafe(() => banner.split('//').join(' ; ').split(' ; ').filter(Boolean), undefined);
        const bannerJSON = {};

        // Loop through bannerString array
        bannerString.forEach((element) => {
            // Classification
            if (classificationOptionsOrder.includes(element)) {
                bannerJSON['classification'] = element;
                return;
            }

            // Check if Element contains '/'
            const subElements = Utilities.getSafe(() => element.split('/').filter(Boolean), undefined);
            subElements.forEach((subElement) => {
                // SCI Options: //SI/TK
                if (sciOptionsOrder.includes(subElement)) {
                    // Push Element into sciOptions Array or initiliaze if empty
                    (bannerJSON['sciOptions'] = bannerJSON['sciOptions'] || []).push(subElement);
                    return;
                }

                // FGI Options: //FGI AUS GBR
                if (subElement.includes('FGI')) {
                    // Grab FGI Markings and place in fgiOptions Array
                    const fgiElements = Utilities.getSafe(() => subElement.split(' ').filter(Boolean).map((item)=> item.trim()), undefined);
                    // Add Items to figOptions: Skip first element 'FGI'
                    fgiElements.filter((e, index) => index !== 0).forEach( (country) => {
                        (bannerJSON['fgiOptions'] = bannerJSON['fgiOptions'] || []).push(country);
                    });
                    return;
                }

                // Dissemination Options: //RSEN/IMCON/REL TO USA/FISA/LES/ORCON-USGOV
                // REL TO needs to be handled as a special case as Country codes are included in the Banner String
                if (disseminationOptionsOrder.includes(subElement) || subElement.includes('REL TO')) {
                    // Push Element into disseminationOptions Array / initiliaze if empty
                    // If REL TO add items to relToOptions
                    if (subElement.includes('REL TO')) {
                        (bannerJSON['disseminationOptions'] = bannerJSON['disseminationOptions'] || []).push('REL TO');
                        // Remove REL TO from String
                        subElement = subElement.replace('REL TO', '');
                        // Grab REL TO Markings and place in relToOptions Array
                        // REL TO Options: REL TO USA, FVEY
                        const relToElements = Utilities.getSafe(() => subElement.split(',').filter(Boolean).map((item)=> item.trim()), undefined);
                        // Add Items to relToOptions
                        relToElements.forEach( (country) => {
                            (bannerJSON['relToOptions'] = bannerJSON['relToOptions'] || []).push(country);
                        });
                    } else {
                        (bannerJSON['disseminationOptions'] = bannerJSON['disseminationOptions'] || []).push(subElement);
                    }
                    return;
                }
            });

            // Once we hit this point, we should only have Sigint options remaining
            const subIntelElements = Utilities.getSafe(() => element.split(':').filter(Boolean), undefined);
            subIntelElements.forEach((subIntelElement) => {
                // Initialize 'intelOptions'
                (bannerJSON['intelOptions'] = bannerJSON['intelOptions'] || []);
                // Sigint:  ; SIGINT:Raw ;
                if (subIntelElement === 'Raw') {
                    bannerJSON['intelOptions']['isRaw'] = true;
                } else if (subIntelElement === 'Is Evaluated And Minimized') {
                    bannerJSON['intelOptions']['isEvaluatedAndMinimized'] = true;
                } else if (subIntelElement === 'Is Disseminable') {
                    bannerJSON['intelOptions']['isDisseminable'] = true;
                }

                // Lacs:  ; LAC12333_S
                if (lacOptionsOrder.includes(element)) {
                    (bannerJSON['intelOptions']['lacOptions'] = bannerJSON['intelOptions']['lacOptions'] || []).push(element);
                    return;
                }
            });
        });

        return bannerJSON;
    }

    // Takes a Classification Banner JSON Object and Returns a String
    static JSONBannerToString(bannerObject): string {
        let bannerString = '';

        // Classification
        const classification = Utilities.getSafe(() => bannerObject.classification, undefined);
        if (classification !== undefined) {
            bannerString += classification;
        }

        // SCI Options
        const SCIItems = Utilities.getSafe(() => bannerObject.sciOptions, undefined);
        if (SCIItems !== undefined && SCIItems.length !== 0) {
            const scis = SCIItems.map( (sci) => sci).join('/');
            bannerString += '//' + scis;
        }

        // FGI Options
        const FGIItems = Utilities.getSafe(() => bannerObject.fgiOptions, undefined);
        if (FGIItems !== undefined && FGIItems.length !== 0) {
            const fgis = FGIItems.map( (fgi) => fgi).join(' ');
            bannerString += '//FGI ' + fgis;
        }

        // Dissemination Options
        // Get Array of dissemination Items ['FOUO', 'REL TO', 'LIMDIS']
        const disseminationItems = Utilities.getSafe(() => bannerObject.disseminationOptions, undefined);
        if (disseminationItems !== undefined) {
            // Build out banner string
            // REL TO shows list of countires separated by a ', '
            bannerString += '//' + disseminationItems.map((obj) => {
                if (obj === 'REL TO') {
                    return obj + ' ' + bannerObject.relToOptions.map((country) => country).join(', ');
                } else {
                    return obj;
                }
            }).join('/');
        }

        // SIGINT Options
        const intelItems = Utilities.getSafe(() => bannerObject.intelOptions, undefined);
        if (intelItems !== undefined) {
            if (intelItems.isEvaluatedAndMinimized) {
                bannerString += ' ; SIGINT:Is Evaluated And Minimized';
                if (Utilities.getSafe(() => intelItems.isDisseminable, undefined) !== undefined && intelItems.isDisseminable) {
                    bannerString += ' ; Is Disseminable';
                }
                if (Utilities.getSafe(() => intelItems.lacOptions, undefined) !== undefined) {
                    let lacs = '';
                    if (intelItems.lacOptions!.length !== 0) {
                        lacs = intelItems.lacOptions!.join(' ; ');
                        bannerString += ' ; ; '+lacs;
                    }
                }
            }
        }
        return bannerString;
    }

    static isValidJSON(jsonString: string): boolean {
        // <TODO> Incorporate Validator Class Instead of hardcoded checks
        let isValid = false;
        // JSON Check
        try {
            const data = JSON.parse(jsonString);
            // Classification Minimum: classification, classificationDate, DeclassificationDate
            if (data['classification'] && data['classificationDate'] && data['declassificationDate']) {
                isValid = true;
            }
        } catch (error) {
            // not a valid JSON Object
        }
        return isValid;
    }

    static isValidXML(xmlString: string, xmlTag: string): boolean {
        let isValid = false;
        // Grabs all XML Tags in a string <tag></tag>
        const tags = xmlString.match(/<[^>]+>/gm);
        // String off <, </, and >
        xmlTag = xmlTag.replace(/<\//g, '').replace(/</g, '').replace(/>/g, '');
        const xmlStartTag = '<' + xmlTag;
        const xmlEndTag = '</' + xmlTag;

        // If Xml String begins and ends with the tag we passed in, good chance xml is valid
        // xmlTag='<tag>' xmlString='<tag><s_tag></s_tag><tag>' : isValid=true
        // xmlTag='<s_tag>' xmlString='<tag><s_tag></s_tag><tag>' : isValid=false
        if (tags !== null && tags!.length !== 0) {
        // XML String begins and ends with xmlTag
            if (tags![0].includes(xmlStartTag) && tags![tags!.length-1].includes(xmlEndTag)) {
                isValid = true;
            }
        }
        return isValid;
    }

    // Extract nested groupings using defined start and end tags
    // Returns matches starting from the end of a String
    static extractGroupsFromTextReverse(str: string, startPattern: string, endPattern: string): string[] {
        let groups!: string[];

        if (str) {
            let firstMatch = str.indexOf(startPattern);
            let lastMatch = str.lastIndexOf(endPattern);
            while (firstMatch < lastMatch) {
                while (firstMatch != -1 && firstMatch < lastMatch) {
                    (groups = groups || []).push(str.substring(firstMatch, lastMatch + endPattern.length));
                    // Get the next matching start pattern
                    firstMatch = str.indexOf(startPattern, firstMatch + endPattern.length);
                }
                // reset startPattern Match to beginning
                firstMatch = str.indexOf(startPattern);
                // decresae endPattern Match
                lastMatch = str.substring(0, lastMatch).lastIndexOf(endPattern);
            }
        }
        return groups;
    }

    // Extracts XML License Info from XML Type
    static extractClassificationXMLFromLicenseInfo(licenseInfo: string): string {
        const groups = Utilities.extractGroupsFromTextReverse(licenseInfo, '<class:Classification ', '</class:Classification>');
        let extractedInfo;
        if (groups !== undefined && groups.length !== 0) {
            groups.forEach((element) => {
                if (Utilities.isValidXML(element, '<class:Classification>')) {
                    extractedInfo = element;
                    return;
                }
            });
        }
        return extractedInfo;
    }

    // Extracts JSON License Info from JSON Type
    static extractClassificationJSONFromLicenseInfo(licenseInfo: string): string {
        const groups = Utilities.extractGroupsFromTextReverse(licenseInfo, '{', '}');
        let extractedInfo;
        if (groups !== undefined && groups.length !== 0) {
            groups.forEach((element) => {
                if (Utilities.isValidJSON(element)) {
                    extractedInfo = element;
                    return;
                }
            });
        }
        return extractedInfo;
    }

    // Extracts JSON License Info from either XML or JSON Types
    static extractJSONFromLicenseInfoTypes(licenseString: string): {'type': string; 'json': string; 'originalClassification': string; 'licenseInfoTermsOfUse': string} {
        const licenseInfoStrUnescaped = Utilities.unescapeHTMLEntities(licenseString);
        let licenseInfoType;
        let licenseInfoJSON;
        let licenseInfoOriginal;
        let licenseInfoTermsOfUse;
        // Try to Extract XML
        let classificationInfo = Utilities.extractClassificationXMLFromLicenseInfo(licenseInfoStrUnescaped);
        if (classificationInfo !== undefined) {
            let tempClassificationXMLObject = new ClassificationXMLObject();
            tempClassificationXMLObject = ClassificationXMLObject.parse(Utilities.xmlToJson(classificationInfo));
            licenseInfoType = 'XML';
            licenseInfoJSON = Utilities.xmlObjectToClassification(tempClassificationXMLObject).serialize();
            licenseInfoOriginal = classificationInfo;
            licenseInfoTermsOfUse = licenseInfoStrUnescaped.replace(licenseInfoOriginal, '');
        } else {
            // No Valid XML Classification String was Found
            // Try to Extract JSON
            classificationInfo = Utilities.extractClassificationJSONFromLicenseInfo(licenseInfoStrUnescaped);
            if (classificationInfo !== undefined) {
                licenseInfoType = 'JSON';
                licenseInfoJSON = classificationInfo;
                licenseInfoOriginal = classificationInfo;
                licenseInfoTermsOfUse = licenseInfoStrUnescaped.replace(licenseInfoOriginal, '');
            }
        }
        return {'type': licenseInfoType, 'json': licenseInfoJSON, 'originalClassification': licenseInfoOriginal, 'licenseInfoTermsOfUse': licenseInfoTermsOfUse};
    }

    // Returns defined options.
    // Primarily used to populate Classification Dialog with Base options.
    static getDefinedOptions(): {classificationOptions: readonly string[]; disseminationOptions: { 'UNCLASSIFIED': readonly string[];
            'CONFIDENTIAL': readonly string[]; 'SECRET': readonly string[]; 'TOP SECRET': readonly string[]; }; relToOptions: readonly string[];
            sciOptions: { 'CONFIDENTIAL': readonly string[]; 'SECRET': readonly string[]; 'TOP SECRET': readonly string[] };
            fgiOptions: readonly string[]; sigintOptions: { 'types': readonly string[] }; lacOptions: { 'acquisitionLacOptions': readonly string[];
            'disseminableLacOptions': readonly string[]; }; topicOptions: ReadonlyArray<{'name': string; 'value': string}>;
            regionOptions: ReadonlyArray<{'value': string; 'name': string}>; } {
        return {
            'classificationOptions': this.getDefinedClassificationOptions(),
            'disseminationOptions': this.getDefinedDisseminationOptions(),
            'relToOptions': this.getDefinedRelToOptions(),
            'sciOptions': this.getDefinedSCIOptions(),
            'fgiOptions': this.getDefinedFGIOptions(),
            'sigintOptions': this.getDefinedSigintOptions(),
            'lacOptions': this.getDefinedLACOptions(),
            'topicOptions': this.getDefinedTopicOptions(),
            'regionOptions': this.getDefinedRegionOptions(),
        };
    }

    // Returns dynamic options.
    // Primarily used to populate Classification Dialog with Base options based on user's accesses.
    static async getDynamicOptions(userID, dynamicURL): Promise<{
        errorMessage: string;
        classificationOptions: readonly string[];
        disseminationOptions: {
            'UNCLASSIFIED': readonly string[];
            'CONFIDENTIAL': readonly string[];
            'SECRET': readonly string[];
            'TOP SECRET': readonly string[];
        };
        relToOptions: readonly string[];
        sciOptions: {
            'CONFIDENTIAL': readonly string[];
            'SECRET': readonly string[];
            'TOP SECRET': readonly string[];
        };
        fgiOptions: readonly string[];
        sigintOptions: {
            'types': readonly string[];
        };
        lacOptions: {
            'acquisitionLacOptions': readonly string[];
            'disseminableLacOptions': readonly string[];
        };
        topicOptions: ReadonlyArray<{
            'name': string;
            'value': string;
        }>;
        regionOptions: ReadonlyArray<{
            'value': string;
            'name': string;
        }>;
    }> {
        try {
            const dynamicResponse = await fetch(dynamicURL+'/getUserProperties/'+userID+'/ALL', {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (dynamicResponse.ok) {
                const jsonResponse = await dynamicResponse.json();
                // const textResponse = JSON.stringify(jsonResponse);
                // TODO: change all these to dynamic classification calls
                if (jsonResponse.sciOptions && jsonResponse.sciOptions.indexOf('NONE') == -1) {
                    // None should always be an option.
                    jsonResponse.sciOptions.unshift('NONE');
                }
                return {
                    'errorMessage': '',
                    'classificationOptions': this.getDynamicClassificationOptions(jsonResponse.classification),
                    'disseminationOptions': this.getDynamicDisseminationOptions(),
                    'relToOptions': this.getDynamicRelToOptions(),
                    'sciOptions': this.getDynamicSCIOptions(jsonResponse.sciOptions),
                    'fgiOptions': this.getDynamicFGIOptions(),
                    'sigintOptions': this.getDynamicSigintOptions(),
                    'lacOptions': this.getDynamicLACOptions(jsonResponse.dissemLacOptions),
                    'topicOptions': this.getDynamicTopicOptions(jsonResponse.topic),
                    'regionOptions': this.getDynamicRegionOptions(jsonResponse.region),
                };
            } else {
                // Return error message So that user knows these are the defaults....
                let errorMessage = 'Error '+ dynamicResponse.status;
                if (dynamicResponse.statusText) {
                    errorMessage += ':' + dynamicResponse.statusText;
                }
                return {
                    'errorMessage': errorMessage,
                    'classificationOptions': this.getDefinedClassificationOptions(),
                    'disseminationOptions': this.getDefinedDisseminationOptions(),
                    'relToOptions': this.getDefinedRelToOptions(),
                    'sciOptions': this.getDefinedSCIOptions(),
                    'fgiOptions': this.getDefinedFGIOptions(),
                    'sigintOptions': this.getDefinedSigintOptions(),
                    'lacOptions': this.getDefinedLACOptions(),
                    'topicOptions': this.getDefinedTopicOptions(),
                    'regionOptions': this.getDefinedRegionOptions(),
                };
            }
        } catch (err) {
            // Promise failed, perhaps because the server is down?
            return {
                'errorMessage': 'Unknown error: '+err,
                'classificationOptions': this.getDefinedClassificationOptions(),
                'disseminationOptions': this.getDefinedDisseminationOptions(),
                'relToOptions': this.getDefinedRelToOptions(),
                'sciOptions': this.getDefinedSCIOptions(),
                'fgiOptions': this.getDefinedFGIOptions(),
                'sigintOptions': this.getDefinedSigintOptions(),
                'lacOptions': this.getDefinedLACOptions(),
                'topicOptions': this.getDefinedTopicOptions(),
                'regionOptions': this.getDefinedRegionOptions(),
            };
        }
    }


    // Targeted Defined Options Getters
    static getDefinedClassificationOptions(): readonly string[] {
        return classificationOptionsOrder;
    }

    static getDefinedDisseminationOptions(): { 'UNCLASSIFIED': readonly string[]; 'CONFIDENTIAL': readonly string[]; 'SECRET': readonly string[]; 'TOP SECRET': readonly string[] } {
        return {
            'UNCLASSIFIED': disseminationUnclassifiedOptions,
            'CONFIDENTIAL': disseminationConfidentialOptions,
            'SECRET': disseminationSecretOptions,
            'TOP SECRET': disseminationTopSecretOptions,
        };
    }

    static getDefinedRelToOptions(): readonly string[] {
        return relToOptionsOrder;
    }

    static getDefinedSCIOptions(): { 'CONFIDENTIAL': readonly string[]; 'SECRET': readonly string[]; 'TOP SECRET': readonly string[] } {
        return {
            'CONFIDENTIAL': sciConfidentialOptions,
            'SECRET': sciSecretOptions,
            'TOP SECRET': sciTopSecretOptions,
        };
    }

    static getDefinedFGIOptions(): readonly string[] {
        return fgiOptionsOrder;
    }

    static getDefinedSigintOptions(): { 'types': readonly string[] } {
        return {'types': sigintOptionsOrder};
    }

    static getDefinedLACOptions(): { 'acquisitionLacOptions': readonly string[]; 'disseminableLacOptions': readonly string[] } {
        return {
            'acquisitionLacOptions': acquisitionLacOptions,
            'disseminableLacOptions': disseminableLacOptions,
        };
    }

    static getDefinedTopicOptions(): ReadonlyArray<{'value': string; 'name': string}> {
        return topicOptionsOrder;
    }

    static getDefinedRegionOptions(): ReadonlyArray<{'value': string; 'name': string}> {
        return regionOptionsOrder;
    }

    // Targeted Dynamic Options Getters
    static getDynamicClassificationOptions(dynamicClassification): readonly string[] {
        let resultclassificationOptionsOrder = [...classificationOptionsOrder];
        if (dynamicClassification.includes('TS')) {
            resultclassificationOptionsOrder = resultclassificationOptionsOrder.slice(0, 4);
        } else if (dynamicClassification.includes('S')) {
            resultclassificationOptionsOrder = resultclassificationOptionsOrder.slice(0, 3);
        } else if (dynamicClassification.includes('C')) {
            resultclassificationOptionsOrder = resultclassificationOptionsOrder.slice(0, 2);
        } else if (dynamicClassification.includes('U')) {
            resultclassificationOptionsOrder = resultclassificationOptionsOrder.slice(0, 1);
        }

        return resultclassificationOptionsOrder;
    }

    static getDynamicDisseminationOptions(): { 'UNCLASSIFIED': readonly string[]; 'CONFIDENTIAL': readonly string[]; 'SECRET': readonly string[]; 'TOP SECRET': readonly string[] } {
        // TODO: Replace with API call
        return {
            'UNCLASSIFIED': disseminationUnclassifiedOptions,
            'CONFIDENTIAL': disseminationConfidentialOptions,
            'SECRET': disseminationSecretOptions,
            'TOP SECRET': disseminationTopSecretOptions,
        };
    }

    static getDynamicRelToOptions(): readonly string[] {
        // TODO: Replace with API call
        return relToOptionsOrder;
    }

    static getDynamicSCIOptions(dynamicSCIOptions): { 'CONFIDENTIAL': readonly string[]; 'SECRET': readonly string[]; 'TOP SECRET': readonly string[] } {
        let returnSCIConfidentialOptions = [''];
        let returnSCISecretOptions = [''];
        let returnSCITopSecretOptions = [''];
        if (dynamicSCIOptions) {
            returnSCIConfidentialOptions = sciConfidentialOptions.filter((value) => dynamicSCIOptions.includes(value));
            returnSCISecretOptions = sciSecretOptions.filter((value) => dynamicSCIOptions.includes(value));
            returnSCITopSecretOptions = sciTopSecretOptions.filter((value) => dynamicSCIOptions.includes(value));
        }

        return {
            'CONFIDENTIAL': returnSCIConfidentialOptions,
            'SECRET': returnSCISecretOptions,
            'TOP SECRET': returnSCITopSecretOptions,
        };
    }

    static getDynamicFGIOptions(): readonly string[] {
        // TODO: Replace with API call
        return fgiOptionsOrder;
    }

    static getDynamicSigintOptions(): { 'types': readonly string[] } {
        // TODO: Replace with API call
        return {'types': sigintOptionsOrder};
    }

    static getDynamicLACOptions(dynamicLACs): { 'acquisitionLacOptions': readonly string[]; 'disseminableLacOptions': readonly string[] } {
        let returnacquisitionLacOptions = [''];
        let returndisseminableLacOptions = [''];
        if (dynamicLACs) {
            returnacquisitionLacOptions = acquisitionLacOptions.filter((value) => dynamicLACs.includes(value));
            returndisseminableLacOptions = disseminableLacOptions.filter((value) => dynamicLACs.includes(value));
        }
        return {
            'acquisitionLacOptions': returnacquisitionLacOptions,
            'disseminableLacOptions': returndisseminableLacOptions,
        };
    }

    static getDynamicTopicOptions(dynamicTopics): ReadonlyArray<{'name': string; 'value': string}> {
        if (dynamicTopics.includes('ANY')) {
            return topicOptionsOrder;
        } else {
            // Dynamic topics are all upper case
            const returnTopics = topicOptionsOrder.filter((topic) => dynamicTopics.includes(topic.value.toUpperCase()));
            return returnTopics;
        }
    }

    static getDynamicRegionOptions(dynamicRegions): ReadonlyArray<{'value': string; 'name': string}> {
        if (dynamicRegions.includes('ANY')) {
            return regionOptionsOrder;
        } else {
            // Dynamic regions are all upper case
            const returnRegions = regionOptionsOrder.filter((region) => dynamicRegions.includes(region.value.toUpperCase()));
            return returnRegions;
        }
    }

    static saveAsJSONOrXML(classificationObject: ClassificationObject & ClassificationXMLObject, output: string): string {
        const classObject: string = classificationObject.getClassName();
        let classificationString = '';
        switch (output.toUpperCase()) {
        case 'JSON':
        default:
            if (classObject == 'ClassificationObject') {
                classificationString = classificationObject.serialize();
            } else if (classObject == 'ClassificationXMLObject') {
                classificationString = classificationObject.toClassificationJSONObject();
            }
            break;
        case 'XML':
            if (classObject == 'ClassificationObject') {
                classificationString = classificationObject.toClassificationXMLString();
            } else if (classObject == 'ClassificationXMLObject') {
                classificationString = this.xmlObjectToClassification(classificationObject).toClassificationXMLString();
            }
            break;
        }
        return classificationString;
    }

    // Takes classification banner and truncates it
    static getTruncatedBannerString(bannerString: string): string {
        // DoDM 5200.01 Vol 2
        // Classification: UNCLASSIFIED: U; CONFIDENTIAL: C; SECRET: S; TOP SECRET: TS
        bannerString = bannerString.replace('UNCLASSIFIED', 'U').replace('CONFIDENTIAL', 'C').replace('TOP SECRET', 'TS').replace('SECRET', 'S');

        // Portion Markings: EXDIS: XD; NODIS: ND; IMCON: IMC; NOFORN: NF; ORCON: OC; PROPIN: PR;
        bannerString = bannerString.replace('EXDIS', 'XD').replace('NODIS', 'ND').replace('IMCON', 'IMC').replace('NOFORN', 'NF').replace('ORCON', 'OC').replace('PROPIN', 'PR');

        // NOT IMPLEMENTED: FORMERLY RESTRICTED DATA: FRD; RESTRICTED DATA: RD; SIGMA: SG;

        return bannerString;
    }
}
